import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Nfc, Users, CheckCircle2, ShieldAlert, Loader2 } from "lucide-react";
import { PageLoading } from "@/components/shared";
import api from "@/lib/api";

const RANGES = [
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 0, label: "all" },
];

const REASON_STYLE = {
  NO_SITE_ACCESS: "bg-red-100 text-red-700",
  ACCESS_DISABLED: "bg-slate-200 text-slate-700",
  PIN_INVALID: "bg-amber-100 text-amber-700",
  PIN_LOCKED: "bg-orange-100 text-orange-700",
};

export default function TimeTagAnalytics() {
  const { t } = useTranslation();
  const [acc, setAcc] = useState(undefined);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((d) => {
    setLoading(true);
    api.get(`/time/hardware/analytics?days=${d}`)
      .then((r) => setData(r.data)).catch(() => setData({ summary: {}, tags: [] }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get("/employer/module/TIME_TRACKING").then((r) => setAcc(r.data?.access || "none")).catch(() => setAcc("none"));
  }, []);
  useEffect(() => { if (acc === "full") load(days); }, [acc, days, load]);

  if (acc === undefined) return <PageLoading />;
  if (acc !== "full") return <Navigate to="/app/employer/time/upgrade" replace />;

  const s = data?.summary || {};
  const dbr = s.denied_by_reason || {};

  const Stat = ({ icon: Icon, tone, value, label, tid }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={tid}>
      <div className={`mb-2 inline-flex h-9 w-9 items-center justify-center rounded-xl ${tone}`}><Icon className="h-5 w-5" /></div>
      <div className="font-heading text-2xl font-extrabold text-navy">{value ?? 0}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );

  return (
    <div data-testid="tag-analytics">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Link to="/app/employer/time/hardware" className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="ta-back"><ArrowLeft className="h-4 w-4" /> {t("ta.back")}</Link>
        <h1 className="ml-2 flex items-center gap-2 font-heading text-2xl font-extrabold text-navy"><Nfc className="h-6 w-6 text-cyan-dark" /> {t("ta.title")}</h1>
        <div className="ml-auto inline-flex overflow-hidden rounded-full border border-slate-200" data-testid="ta-range">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setDays(r.key)}
              className={`px-3 py-1.5 text-xs font-semibold ${days === r.key ? "bg-navy text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              data-testid={`ta-range-${r.key}`}>{t(`ta.range_${r.label}`)}</button>
          ))}
        </div>
      </div>
      <p className="mb-6 text-sm text-slate-500">{t("ta.subtitle")}</p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Nfc} tone="bg-cyan/15 text-cyan-dark" value={s.active_tags} label={t("ta.activeTags")} tid="ta-active-tags" />
        <Stat icon={CheckCircle2} tone="bg-emerald-100 text-emerald-700" value={s.total_clock_ins} label={t("ta.clockIns")} tid="ta-clock-ins" />
        <Stat icon={Users} tone="bg-navy/10 text-navy" value={s.unique_workers} label={t("ta.uniqueWorkers")} tid="ta-unique-workers" />
        <Stat icon={ShieldAlert} tone="bg-red-100 text-red-700" value={s.denied_total} label={t("ta.deniedTotal")} tid="ta-denied-total" />
      </div>

      {/* Denied by reason */}
      <h2 className="mb-3 mt-8 font-heading text-lg font-bold text-navy">{t("ta.deniedByReason")}</h2>
      <div className="flex flex-wrap gap-2" data-testid="ta-denied-reasons">
        {Object.keys(REASON_STYLE).map((r) => (
          <span key={r} className={`rounded-full px-3 py-1 text-xs font-semibold ${REASON_STYLE[r]}`} data-testid={`ta-reason-${r}`}>
            {t(`ta.reason_${r}`)}: {dbr[r] || 0}
          </span>
        ))}
      </div>

      {/* Per-tag table */}
      <h2 className="mb-3 mt-8 flex items-center gap-2 font-heading text-lg font-bold text-navy"><Nfc className="h-5 w-5 text-cyan-dark" /> {t("ta.perTag")}</h2>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="ta-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["colTag", "colSite", "colStatus", "colClocks", "colWorkers", "colDenied", "colLast"].map((c) => <th key={c} className="px-4 py-3">{t(`ta.${c}`)}</th>)}</tr>
          </thead>
          <tbody>
            {loading || !data ? <tr><td colSpan={7} className="p-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr> :
              data.tags.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">{t("ta.empty")}</td></tr> :
                data.tags.map((tg) => (
                  <tr key={tg.id} className="border-b border-slate-50" data-testid={`ta-row-${tg.nfc_tag_id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-navy">{tg.nfc_tag_id}</td>
                    <td className="px-4 py-3 text-slate-600">{tg.site_name}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tg.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : tg.status === "NEEDS_REPROGRAMMING" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{tg.status}</span></td>
                    <td className="px-4 py-3 font-semibold text-navy">{tg.clock_ins}</td>
                    <td className="px-4 py-3 text-slate-600">{tg.unique_workers}</td>
                    <td className="px-4 py-3">{tg.denied_on_site > 0 ? <span className="font-semibold text-red-600">{tg.denied_on_site}</span> : <span className="text-slate-400">0</span>}</td>
                    <td className="px-4 py-3 text-slate-500">{tg.last_clock_at ? new Date(tg.last_clock_at).toLocaleString() : t("ta.never")}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
